import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, BarChart3, Users, Trash2, Eye, Mail, Download, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, XCircle, Activity, ArrowLeft, Settings2 } from 'lucide-react';

const STOCK_PROFILES = {
  dairy_mature: { label: 'Mature Cows', target: 5.0, attention: 4.0, over_conditioned: 5.5, critical: 3.0, min_weight: 0 },
  dairy_heifer: { label: 'Heifers', target: 5.5, attention: 4.5, over_conditioned: 6.0, critical: 3.0, min_weight: 0 },
  beef: { label: 'Beef Cattle', target: 6.0, attention: 5.0, over_conditioned: 7.5, critical: 3.0, min_weight: 0 },
};

const BREED_OPTIONS = [
  { value: '', label: '-- Select Breed --' },
  { value: 'jersey', label: 'Jersey (Smaller Frame)' },
  { value: 'crossbred', label: 'Crossbred (Medium Frame)' },
  { value: 'nz_holstein_friesian', label: 'NZ Holstein Friesian (Medium Frame)' },
  { value: 'overseas_holstein_friesian', label: 'Overseas Holstein Friesian (Large Frame)' },
];

// ─── Status Badge ───
const StatusBadge = ({ status, label }) => {
  const styles = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    attention: 'bg-amber-100 text-amber-800 border-amber-300',
    monitor: 'bg-blue-100 text-blue-800 border-blue-300',
    good: 'bg-green-100 text-green-800 border-green-300',
    over_conditioned: 'bg-purple-100 text-purple-800 border-purple-300',
    no_bcs: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${styles[status] || styles.no_bcs}`}>
      {label}
    </span>
  );
};

// ─── Simple Bar Chart ───
const SimpleBarChart = ({ data, label, color = '#1a4d2e', maxBars = 20 }) => {
  const entries = Object.entries(data).slice(0, maxBars);
  const maxVal = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div data-testid={`chart-${label}`}>
      <h4 className="text-sm font-semibold text-[#1a4d2e] mb-2">{label}</h4>
      <div className="space-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="flex items-center gap-2 text-xs">
            <span className="w-16 text-right text-gray-600 shrink-0">{key}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 text-white font-semibold"
                style={{ width: `${Math.max((val / maxVal) * 100, 8)}%`, backgroundColor: color }}
              >
                {val}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Stat Card ───
const StatCard = ({ label, value, sub, icon: Icon, color = 'text-[#1a4d2e]' }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

// ════════════════════════════════════════════
// MAIN REPORTS TAB
// ════════════════════════════════════════════
const ReportsTab = ({ BACKEND_URL }) => {
  const [view, setView] = useState('list'); // list | upload | report | config
  const [imports, setImports] = useState([]);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadClientEmail, setUploadClientEmail] = useState('');
  const [uploadClientName, setUploadClientName] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Generate state
  const [selectedImports, setSelectedImports] = useState([]);
  const [stockType, setStockType] = useState('dairy_mature');
  const [generating, setGenerating] = useState(false);

  // Farm settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsClient, setSettingsClient] = useState('');
  const [farmConfig, setFarmConfig] = useState({
    target_bcs: 5.0, attention_below: 4.0, over_conditioned_above: 5.5,
    critical_below: 3.0, min_weight: 0, breed: '', notes: '', stock_type: 'dairy_mature',
  });
  const [configSaving, setConfigSaving] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Report view state
  const [activeReport, setActiveReport] = useState(null);
  const [reportTab, setReportTab] = useState('summary');

  // Registered farmers for linking
  const [registrations, setRegistrations] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [importsRes, reportsRes, clientsRes, regsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/reports/imports`),
        fetch(`${BACKEND_URL}/api/admin/reports/generated`),
        fetch(`${BACKEND_URL}/api/admin/reports/client-list`),
        fetch(`${BACKEND_URL}/api/admin/registrations`),
      ]);
      if (importsRes.ok) setImports(await importsRes.json());
      if (reportsRes.ok) setReports(await reportsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (regsRes.ok) setRegistrations(await regsRes.json());
    } catch (e) {
      console.error('Failed to load report data:', e);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Upload CSV ──
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setError('');
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('client_email', uploadClientEmail);
    formData.append('client_name', uploadClientName);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/upload-csv`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setUploadResult(data);
      fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Delete Import ──
  const handleDeleteImport = async (id) => {
    if (!window.confirm('Delete this import and all its records?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/reports/imports/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (e) { setError(e.message); }
  };

  // ── Generate Report ──
  const handleGenerate = async () => {
    if (selectedImports.length === 0) { setError('Select at least one import'); return; }
    setGenerating(true);
    setError('');
    // Find client info from first selected import
    const firstImp = imports.find(i => i.id === selectedImports[0]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          import_ids: selectedImports,
          client_email: firstImp?.client_email || '',
          client_name: firstImp?.client_name || '',
          stock_type: stockType,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      setActiveReport(data);
      setView('report');
      fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  // ── View Report ──
  const handleViewReport = async (reportId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/generated/${reportId}`);
      if (!res.ok) throw new Error('Failed to load report');
      const data = await res.json();
      setActiveReport(data);
      setView('report');
    } catch (e) { setError(e.message); }
  };

  // ── Delete Report ──
  const handleDeleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/reports/generated/${id}`, { method: 'DELETE' });
      if (activeReport?.id === id) { setActiveReport(null); setView('list'); }
      fetchData();
    } catch (e) { setError(e.message); }
  };

  // ── Email Report ──
  const handleEmailReport = async (reportId) => {
    const report = activeReport || reports.find(r => r.id === reportId);
    const email = prompt('Send report notification to:', report?.client_email || '');
    if (!email) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/generated/${reportId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: email, recipient_name: report?.client_name || '' }),
      });
      if (!res.ok) throw new Error('Failed to send');
      alert('Report notification sent!');
    } catch (e) { alert('Error: ' + e.message); }
  };

  // ── Toggle import selection ──
  const toggleImportSelection = (id) => {
    setSelectedImports(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Load Farm Config ──
  const loadFarmConfig = async (email) => {
    setSettingsClient(email);
    setConfigLoaded(false);
    if (!email) {
      setFarmConfig({ target_bcs: 5.0, attention_below: 4.0, over_conditioned_above: 5.5, critical_below: 3.0, min_weight: 0, breed: '', notes: '', stock_type: 'dairy_mature' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/configs/${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setFarmConfig({
          target_bcs: data.target_bcs ?? 5.0,
          attention_below: data.attention_below ?? 4.0,
          over_conditioned_above: data.over_conditioned_above ?? 5.5,
          critical_below: data.critical_below ?? 3.0,
          min_weight: data.min_weight ?? 0,
          breed: data.breed || '',
          notes: data.notes || '',
          stock_type: data.stock_type || 'dairy_mature',
        });
        setConfigLoaded(!data.default);
      }
    } catch (e) { console.error('Failed to load farm config:', e); }
  };

  // ── Save Farm Config ──
  const saveFarmConfig = async () => {
    if (!settingsClient) { setError('Select a farm to save settings for'); return; }
    setConfigSaving(true);
    const clientReg = registrations.find(r => r.email === settingsClient);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_email: settingsClient,
          client_name: clientReg?.customer_name || clientReg?.farm_name || '',
          ...farmConfig,
        }),
      });
      if (!res.ok) throw new Error('Failed to save');
      setConfigLoaded(true);
      setError('');
      alert('Farm settings saved successfully!');
    } catch (e) { setError(e.message); } finally { setConfigSaving(false); }
  };

  // ── Apply stock profile preset ──
  const applyPreset = (profileKey) => {
    const p = STOCK_PROFILES[profileKey];
    if (p) {
      setFarmConfig(prev => ({
        ...prev,
        stock_type: profileKey,
        target_bcs: p.target,
        attention_below: p.attention,
        over_conditioned_above: p.over_conditioned,
        critical_below: p.critical,
      }));
    }
  };

  // ═══ REPORT VIEW ═══
  if (view === 'report' && activeReport) {
    const rd = activeReport.report_data || {};
    const summary = rd.summary || {};
    const ws = rd.weight_stats || {};
    const bs = rd.bcs_stats || {};
    const thresholds = rd.thresholds || {};
    const statusCounts = rd.status_counts || {};
    const allRecords = rd.all_records || [];
    const attentionAnimals = rd.attention_animals || [];
    const overConditionedAnimals = rd.over_conditioned_animals || [];
    const underweightAnimals = rd.underweight_animals || [];
    const weightTargets = rd.weight_targets || [];
    const flaggedCount = attentionAnimals.length + overConditionedAnimals.length;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="report-viewer">
        {/* Report Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); setActiveReport(null); }} className="text-gray-500 hover:text-[#1a4d2e]">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-[#1a4d2e]">{rd.client_name || 'Farm Report'}</h2>
              <p className="text-sm text-gray-500">
                {rd.profile?.label || 'Custom'} &bull; Target BCS: {thresholds.target_bcs}
                {rd.profile?.breed && <> &bull; {BREED_OPTIONS.find(b => b.value === rd.profile.breed)?.label || rd.profile.breed}</>}
                {' '}&bull; Generated {new Date(activeReport.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => handleEmailReport(activeReport.id)} className="flex items-center gap-1 px-3 py-2 text-sm bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d6b47]" data-testid="email-report-btn">
              <Mail className="w-4 h-4" /> Email
            </button>
            <button onClick={() => window.print()} className="flex items-center gap-1 px-3 py-2 text-sm bg-[#ff6b00] text-white rounded-lg hover:bg-[#ff6b00]/90" data-testid="print-report-btn">
              <Download className="w-4 h-4" /> Print / PDF
            </button>
            <button onClick={() => handleDeleteReport(activeReport.id)} className="flex items-center gap-1 px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Report Tabs */}
        <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm mb-6 overflow-x-auto">
          {['summary', 'animals', 'attention', 'targets'].map(tab => (
            <button key={tab} onClick={() => setReportTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${reportTab === tab ? 'bg-[#1a4d2e] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`report-tab-${tab}`}
            >
              {tab === 'summary' && 'Summary'}
              {tab === 'animals' && `All Animals (${summary.total_animals || 0})`}
              {tab === 'attention' && `Flagged (${flaggedCount})`}
              {tab === 'targets' && `Weight Targets (${weightTargets.length})`}
            </button>
          ))}
        </div>

        {/* ── SUMMARY TAB ── */}
        {reportTab === 'summary' && (
          <div className="space-y-6 print-section" data-testid="report-summary">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Animals" value={summary.total_animals || 0} icon={Users} />
              <StatCard label="Avg Weight" value={ws.average ? `${ws.average} kg` : 'N/A'} sub={ws.count ? `${ws.count} weighed` : ''} icon={Activity} />
              <StatCard label="Avg BCS" value={bs.average || 'N/A'} sub={bs.count ? `${bs.count} scored` : ''} icon={BarChart3} color="text-[#ff6b00]" />
              <StatCard label="Matched" value={summary.matched_records || 0} sub="Weight + BCS" icon={CheckCircle2} color="text-green-600" />
            </div>

            {/* Status Overview */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-[#1a4d2e] mb-4">Herd Status Overview</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-2xl font-bold text-green-700">{statusCounts.good || 0}</p>
                  <p className="text-xs text-green-600 font-medium">On Target</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl font-bold text-blue-700">{statusCounts.monitor || 0}</p>
                  <p className="text-xs text-blue-600 font-medium">Monitor</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-2xl font-bold text-amber-700">{statusCounts.attention || 0}</p>
                  <p className="text-xs text-amber-600 font-medium">Below {thresholds.attention_below}</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-2xl font-bold text-red-700">{statusCounts.critical || 0}</p>
                  <p className="text-xs text-red-600 font-medium">Vet Required (&lt;{thresholds.critical_below})</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-2xl font-bold text-purple-700">{statusCounts.over_conditioned || 0}</p>
                  <p className="text-xs text-purple-600 font-medium">Over Conditioned (&gt;{thresholds.over_conditioned_above})</p>
                </div>
                {thresholds.min_weight > 0 && (
                  <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-2xl font-bold text-orange-700">{summary.underweight || 0}</p>
                    <p className="text-xs text-orange-600 font-medium">Underweight (&lt;{thresholds.min_weight}kg)</p>
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Target BCS: {thresholds.target_bcs} &bull; Attention below: {thresholds.attention_below} &bull; Over conditioned above: {thresholds.over_conditioned_above} &bull; Vet below: {thresholds.critical_below}
                {thresholds.min_weight > 0 && <> &bull; Min weight: {thresholds.min_weight}kg</>}
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {Object.keys(rd.bcs_distribution || {}).length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <SimpleBarChart data={rd.bcs_distribution} label="BCS Distribution" color="#ff6b00" />
                </div>
              )}
              {Object.keys(rd.weight_distribution || {}).length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <SimpleBarChart data={rd.weight_distribution} label="Weight Distribution (kg)" color="#1a4d2e" />
                </div>
              )}
            </div>

            {/* Weight & BCS Stats Detail */}
            <div className="grid md:grid-cols-2 gap-6">
              {ws.count > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-semibold text-[#1a4d2e] mb-3">Weight Statistics</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Count:</span> <span className="font-semibold">{ws.count}</span></div>
                    <div><span className="text-gray-500">Average:</span> <span className="font-semibold">{ws.average} kg</span></div>
                    <div><span className="text-gray-500">Median:</span> <span className="font-semibold">{ws.median} kg</span></div>
                    <div><span className="text-gray-500">Std Dev:</span> <span className="font-semibold">{ws.std_dev} kg</span></div>
                    <div><span className="text-gray-500">Min:</span> <span className="font-semibold">{ws.min} kg</span></div>
                    <div><span className="text-gray-500">Max:</span> <span className="font-semibold">{ws.max} kg</span></div>
                  </div>
                </div>
              )}
              {bs.count > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-semibold text-[#1a4d2e] mb-3">BCS Statistics</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Count:</span> <span className="font-semibold">{bs.count}</span></div>
                    <div><span className="text-gray-500">Average:</span> <span className="font-semibold">{bs.average}</span></div>
                    <div><span className="text-gray-500">Median:</span> <span className="font-semibold">{bs.median}</span></div>
                    <div><span className="text-gray-500">Std Dev:</span> <span className="font-semibold">{bs.std_dev}</span></div>
                    <div><span className="text-gray-500">Min:</span> <span className="font-semibold">{bs.min}</span></div>
                    <div><span className="text-gray-500">Max:</span> <span className="font-semibold">{bs.max}</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ALL ANIMALS TAB ── */}
        {reportTab === 'animals' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="report-animals">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#1a4d2e] text-white">
                  <tr>
                    <th className="px-3 py-2 text-left">LID</th>
                    <th className="px-3 py-2 text-left">EID</th>
                    <th className="px-3 py-2 text-left">Tag/ID</th>
                    <th className="px-3 py-2 text-right">Weight (kg)</th>
                    <th className="px-3 py-2 text-right">BCS</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">BCS Gap</th>
                    <th className="px-3 py-2 text-right">Wt Gain Needed</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.map((rec, idx) => (
                    <tr key={idx} className={`border-b border-gray-100 ${rec.status === 'critical' ? 'bg-red-50' : rec.status === 'attention' ? 'bg-amber-50' : rec.status === 'over_conditioned' ? 'bg-purple-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-3 py-2 font-mono text-xs">{rec.lid || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{rec.eid || '-'}</td>
                      <td className="px-3 py-2 font-mono font-semibold">{rec.animal_id || '-'}</td>
                      <td className={`px-3 py-2 text-right ${rec.underweight ? 'text-orange-700 font-bold' : ''}`}>{rec.weight != null ? rec.weight : '-'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{rec.bcs_score != null ? rec.bcs_score : '-'}</td>
                      <td className="px-3 py-2 text-center"><StatusBadge status={rec.status} label={rec.status_label} /></td>
                      <td className="px-3 py-2 text-right">{rec.bcs_gap > 0 ? rec.bcs_gap : '-'}</td>
                      <td className="px-3 py-2 text-right">{rec.weight_gain_needed > 0 ? `${rec.weight_gain_needed} kg` : '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{rec.bcs_reason || rec.notes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ATTENTION TAB ── */}
        {reportTab === 'attention' && (
          <div data-testid="report-attention">
            {flaggedCount === 0 && underweightAnimals.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-semibold">All animals are within target ranges!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attentionAnimals.filter(a => a.status === 'critical').length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                      <XCircle className="w-5 h-5" /> Vet Required (BCS below {thresholds.critical_below})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-red-700"><th className="px-2 py-1">Tag</th><th className="px-2 py-1 text-right">BCS</th><th className="px-2 py-1 text-right">Weight</th><th className="px-2 py-1">Notes</th></tr></thead>
                        <tbody>
                          {attentionAnimals.filter(a => a.status === 'critical').map((a, i) => (
                            <tr key={i} className="border-t border-red-200">
                              <td className="px-2 py-1 font-mono font-semibold">{a.animal_id}</td>
                              <td className="px-2 py-1 text-right font-bold text-red-700">{a.bcs_score}</td>
                              <td className="px-2 py-1 text-right">{a.weight != null ? `${a.weight} kg` : '-'}</td>
                              <td className="px-2 py-1 text-xs">{a.bcs_reason || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {attentionAnimals.filter(a => a.status === 'attention').length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" /> Needs Attention (BCS below {thresholds.attention_below})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-amber-700"><th className="px-2 py-1">Tag</th><th className="px-2 py-1 text-right">BCS</th><th className="px-2 py-1 text-right">Weight</th><th className="px-2 py-1 text-right">Gap to Target</th><th className="px-2 py-1">Notes</th></tr></thead>
                        <tbody>
                          {attentionAnimals.filter(a => a.status === 'attention').map((a, i) => (
                            <tr key={i} className="border-t border-amber-200">
                              <td className="px-2 py-1 font-mono font-semibold">{a.animal_id}</td>
                              <td className="px-2 py-1 text-right font-bold text-amber-700">{a.bcs_score}</td>
                              <td className="px-2 py-1 text-right">{a.weight != null ? `${a.weight} kg` : '-'}</td>
                              <td className="px-2 py-1 text-right">{a.bcs_gap > 0 ? `${a.bcs_gap} units` : '-'}</td>
                              <td className="px-2 py-1 text-xs">{a.bcs_reason || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {overConditionedAnimals.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" /> Over Conditioned (BCS above {thresholds.over_conditioned_above})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-purple-700"><th className="px-2 py-1">Tag</th><th className="px-2 py-1 text-right">BCS</th><th className="px-2 py-1 text-right">Weight</th><th className="px-2 py-1">Notes</th></tr></thead>
                        <tbody>
                          {overConditionedAnimals.map((a, i) => (
                            <tr key={i} className="border-t border-purple-200">
                              <td className="px-2 py-1 font-mono font-semibold">{a.animal_id}</td>
                              <td className="px-2 py-1 text-right font-bold text-purple-700">{a.bcs_score}</td>
                              <td className="px-2 py-1 text-right">{a.weight != null ? `${a.weight} kg` : '-'}</td>
                              <td className="px-2 py-1 text-xs">{a.bcs_reason || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {underweightAnimals.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-800 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" /> Underweight (below {thresholds.min_weight}kg minimum)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-orange-700"><th className="px-2 py-1">Tag</th><th className="px-2 py-1 text-right">Weight</th><th className="px-2 py-1 text-right">BCS</th><th className="px-2 py-1 text-right">Below Min By</th></tr></thead>
                        <tbody>
                          {underweightAnimals.map((a, i) => (
                            <tr key={i} className="border-t border-orange-200">
                              <td className="px-2 py-1 font-mono font-semibold">{a.animal_id}</td>
                              <td className="px-2 py-1 text-right font-bold text-orange-700">{a.weight} kg</td>
                              <td className="px-2 py-1 text-right">{a.bcs_score != null ? a.bcs_score : '-'}</td>
                              <td className="px-2 py-1 text-right">{(thresholds.min_weight - a.weight).toFixed(1)} kg</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── WEIGHT TARGETS TAB ── */}
        {reportTab === 'targets' && (
          <div data-testid="report-targets">
            {weightTargets.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-600">No weight gain targets — either all animals are on target or weight data is unavailable.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 bg-[#fff8f0] border-b border-[#ff6b00]/20">
                  <p className="text-sm text-[#8d6e63]">
                    <strong className="text-[#1a4d2e]">BCS-Weight Correlation:</strong> Each additional BCS unit requires a liveweight increase of <strong>6.58%</strong> of current weight.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a4d2e] text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">LID</th>
                        <th className="px-3 py-2 text-left">EID</th>
                        <th className="px-3 py-2 text-left">Tag/ID</th>
                        <th className="px-3 py-2 text-right">Current Wt</th>
                        <th className="px-3 py-2 text-right">Current BCS</th>
                        <th className="px-3 py-2 text-right">Target BCS</th>
                        <th className="px-3 py-2 text-right">BCS Gap</th>
                        <th className="px-3 py-2 text-right">Wt Gain Needed</th>
                        <th className="px-3 py-2 text-right">Target Wt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weightTargets.map((rec, idx) => (
                        <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="px-3 py-2 font-mono text-xs">{rec.lid || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{rec.eid || '-'}</td>
                          <td className="px-3 py-2 font-mono font-semibold">{rec.animal_id}</td>
                          <td className="px-3 py-2 text-right">{rec.weight} kg</td>
                          <td className="px-3 py-2 text-right font-semibold text-amber-700">{rec.bcs_score}</td>
                          <td className="px-3 py-2 text-right text-green-700">{thresholds.target_bcs}</td>
                          <td className="px-3 py-2 text-right">{rec.bcs_gap}</td>
                          <td className="px-3 py-2 text-right font-semibold text-[#ff6b00]">+{rec.weight_gain_needed} kg</td>
                          <td className="px-3 py-2 text-right font-semibold text-[#1a4d2e]">{rec.target_weight} kg</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ═══ LIST / UPLOAD VIEW ═══
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="reports-tab">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-lg font-bold text-[#1a4d2e]">CSV Imports & Reports</h2>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowSettings(!showSettings); if (!showSettings) setView('list'); }}
            className={`flex items-center gap-1 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${showSettings ? 'bg-[#ff6b00] text-white' : 'bg-[#fff8f0] text-[#ff6b00] border border-[#ff6b00] hover:bg-[#ff6b00] hover:text-white'}`}
            data-testid="farm-settings-btn"
          >
            <Settings2 className="w-4 h-4" /> Farm Settings
          </button>
          <button
            onClick={() => { setView(view === 'upload' ? 'list' : 'upload'); setUploadResult(null); setUploadFile(null); setShowSettings(false); }}
            className={`flex items-center gap-1 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${view === 'upload' ? 'bg-gray-200 text-gray-700' : 'bg-[#1a4d2e] text-white hover:bg-[#2d6b47]'}`}
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4" /> {view === 'upload' ? 'Cancel' : 'Upload CSV'}
          </button>
        </div>
      </div>

      {/* ── FARM SETTINGS PANEL ── */}
      {showSettings && (
        <div className="bg-white rounded-lg border-2 border-[#ff6b00]/30 p-6 mb-6" data-testid="farm-settings-panel">
          <h3 className="font-semibold text-[#1a4d2e] mb-4 flex items-center gap-2">
            <Settings2 className="w-5 h-5" /> Farm Target Settings
          </h3>
          <p className="text-sm text-gray-500 mb-4">Set custom BCS targets, weight thresholds, and alert levels per farm. These settings will be used when generating reports for the selected farm.</p>

          {/* Select Farm */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Farm</label>
            <select
              value={settingsClient}
              onChange={(e) => loadFarmConfig(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="settings-client-select"
            >
              <option value="">-- Select Registered Farmer --</option>
              {registrations.map(reg => (
                <option key={reg.email} value={reg.email}>
                  {reg.customer_name || reg.email} {reg.farm_name ? `(${reg.farm_name})` : ''}
                </option>
              ))}
            </select>
            {configLoaded && <p className="text-xs text-green-600 mt-1 font-medium">Custom settings loaded for this farm</p>}
          </div>

          {settingsClient && (
            <>
              {/* Preset Profiles */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start from Preset</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STOCK_PROFILES).map(([key, p]) => (
                    <button
                      key={key}
                      onClick={() => applyPreset(key)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${farmConfig.stock_type === key ? 'bg-[#1a4d2e] text-white border-[#1a4d2e]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#1a4d2e]'}`}
                      data-testid={`preset-${key}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Settings Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Breed</label>
                  <select
                    value={farmConfig.breed}
                    onChange={(e) => setFarmConfig(p => ({ ...p, breed: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="config-breed"
                  >
                    {BREED_OPTIONS.map(b => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Threshold Settings Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#1a4d2e] mb-1">Target BCS</label>
                  <input
                    type="number" step="0.5" min="1" max="10"
                    value={farmConfig.target_bcs}
                    onChange={(e) => setFarmConfig(p => ({ ...p, target_bcs: parseFloat(e.target.value) || 5.0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold text-center"
                    data-testid="config-target-bcs"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Ideal BCS score</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-700 mb-1">Attention Below</label>
                  <input
                    type="number" step="0.5" min="1" max="10"
                    value={farmConfig.attention_below}
                    onChange={(e) => setFarmConfig(p => ({ ...p, attention_below: parseFloat(e.target.value) || 4.0 }))}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm font-semibold text-center bg-amber-50"
                    data-testid="config-attention-below"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Flag if BCS below</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-purple-700 mb-1">Over Conditioned Above</label>
                  <input
                    type="number" step="0.5" min="1" max="10"
                    value={farmConfig.over_conditioned_above}
                    onChange={(e) => setFarmConfig(p => ({ ...p, over_conditioned_above: parseFloat(e.target.value) || 5.5 }))}
                    className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm font-semibold text-center bg-purple-50"
                    data-testid="config-over-conditioned"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Flag if BCS above</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-red-700 mb-1">Vet Required Below</label>
                  <input
                    type="number" step="0.5" min="1" max="10"
                    value={farmConfig.critical_below}
                    onChange={(e) => setFarmConfig(p => ({ ...p, critical_below: parseFloat(e.target.value) || 3.0 }))}
                    className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm font-semibold text-center bg-red-50"
                    data-testid="config-critical-below"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Vet visit threshold</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-orange-700 mb-1">Minimum Weight (kg)</label>
                  <input
                    type="number" step="10" min="0" max="1000"
                    value={farmConfig.min_weight}
                    onChange={(e) => setFarmConfig(p => ({ ...p, min_weight: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm font-semibold text-center bg-orange-50"
                    data-testid="config-min-weight"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">0 = no minimum</p>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={farmConfig.notes}
                  onChange={(e) => setFarmConfig(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  rows={2}
                  placeholder="e.g. Breed: Jersey x Friesian, Feed budget considerations..."
                  data-testid="config-notes"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={saveFarmConfig}
                disabled={configSaving}
                className="w-full bg-[#ff6b00] text-white py-3 rounded-lg font-semibold hover:bg-[#ff6b00]/90 disabled:opacity-50 transition-colors"
                data-testid="save-settings-btn"
              >
                {configSaving ? 'Saving...' : 'Save Farm Settings'}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── UPLOAD FORM ── */}
      {view === 'upload' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6" data-testid="upload-form">
          <h3 className="font-semibold text-[#1a4d2e] mb-4 flex items-center gap-2"><Upload className="w-5 h-5" /> Import CSV Data</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link to Farmer / Client</label>
              <select
                value={uploadClientEmail}
                onChange={(e) => {
                  setUploadClientEmail(e.target.value);
                  const reg = registrations.find(r => r.email === e.target.value);
                  if (reg) setUploadClientName(reg.customer_name || reg.farm_name || '');
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                data-testid="upload-client-select"
              >
                <option value="">-- Select Registered Farmer --</option>
                {registrations.map(reg => (
                  <option key={reg.email} value={reg.email}>
                    {reg.customer_name || reg.email} {reg.farm_name ? `(${reg.farm_name})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client / Farm Name</label>
              <input
                type="text"
                value={uploadClientName}
                onChange={(e) => setUploadClientName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Smith Family Farm"
                data-testid="upload-client-name"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1a4d2e] transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1a4d2e] file:text-white hover:file:bg-[#2d6b47]"
                data-testid="upload-file-input"
              />
              <p className="text-xs text-gray-400 mt-2">Supports weighing device CSV and BCS scoring CSV formats</p>
            </div>
          </div>
          <button
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
            className="w-full bg-[#ff6b00] text-white py-3 rounded-lg font-semibold hover:bg-[#ff6b00]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="upload-submit-btn"
          >
            {uploading ? 'Uploading...' : 'Upload & Process CSV'}
          </button>

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="upload-result">
              <p className="text-green-700 font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> {uploadResult.message}
              </p>
              <p className="text-sm text-green-600 mt-1">
                Type: <strong>{uploadResult.import?.import_type}</strong> &bull;
                Records: <strong>{uploadResult.import?.record_count}</strong> &bull;
                Date: <strong>{uploadResult.import?.date_range}</strong>
              </p>
              {uploadResult.preview && uploadResult.preview.length > 0 && (
                <details className="mt-3">
                  <summary className="text-sm text-green-700 cursor-pointer font-medium">Preview first records</summary>
                  <div className="mt-2 overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead><tr className="text-left text-green-700">
                        <th className="px-2 py-1">ID</th>
                        {uploadResult.import?.import_type === 'weighing' && <th className="px-2 py-1">Weight</th>}
                        {uploadResult.import?.import_type === 'bcs' && <th className="px-2 py-1">BCS</th>}
                        <th className="px-2 py-1">Date</th>
                      </tr></thead>
                      <tbody>
                        {uploadResult.preview.map((r, i) => (
                          <tr key={i} className="border-t border-green-200">
                            <td className="px-2 py-1 font-mono">{r.animal_id}</td>
                            {uploadResult.import?.import_type === 'weighing' && <td className="px-2 py-1">{r.weight} kg</td>}
                            {uploadResult.import?.import_type === 'bcs' && <td className="px-2 py-1">{r.bcs_score}</td>}
                            <td className="px-2 py-1">{r.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── IMPORTS LIST ── */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-[#1a4d2e] flex items-center gap-2">
            <FileText className="w-5 h-5" /> Imported Datasets ({imports.length})
          </h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : imports.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Upload className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No CSV imports yet. Upload your first dataset above.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {imports.map(imp => (
              <div key={imp.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selectedImports.includes(imp.id)}
                  onChange={() => toggleImportSelection(imp.id)}
                  className="w-4 h-4 accent-[#1a4d2e]"
                  data-testid={`import-check-${imp.id}`}
                />
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold ${imp.import_type === 'weighing' ? 'bg-[#1a4d2e]' : 'bg-[#ff6b00]'}`}>
                  {imp.import_type === 'weighing' ? 'WT' : 'BCS'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 truncate">{imp.filename}</p>
                  <p className="text-xs text-gray-500">
                    {imp.client_name || imp.client_email || 'Unlinked'} &bull; {imp.record_count} records &bull; {imp.date_range}
                  </p>
                </div>
                <span className="text-xs text-gray-400">{new Date(imp.created_at).toLocaleDateString()}</span>
                <button onClick={() => handleDeleteImport(imp.id)} className="text-gray-400 hover:text-red-500 p-1" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Generate Report Bar */}
        {selectedImports.length > 0 && (
          <div className="p-4 bg-[#f4f4f0] border-t border-gray-200 flex flex-wrap items-center gap-3" data-testid="generate-bar">
            <span className="text-sm font-medium text-[#1a4d2e]">{selectedImports.length} import(s) selected</span>
            <select
              value={stockType}
              onChange={(e) => setStockType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="stock-type-select"
            >
              {Object.entries(STOCK_PROFILES).map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1 px-6 py-2 bg-[#ff6b00] text-white rounded-lg font-semibold text-sm hover:bg-[#ff6b00]/90 disabled:opacity-50"
              data-testid="generate-report-btn"
            >
              <BarChart3 className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        )}
      </div>

      {/* ── GENERATED REPORTS ── */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-[#1a4d2e] flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Generated Reports ({reports.length})
          </h3>
        </div>
        {reports.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No reports generated yet. Select imports above and generate a report.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {reports.map(rpt => (
              <div key={rpt.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                <div className="w-10 h-10 rounded-lg bg-[#1a4d2e] flex items-center justify-center text-white">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800">{rpt.client_name || 'Unnamed Report'}</p>
                  <p className="text-xs text-gray-500">
                    {rpt.stock_type ? STOCK_PROFILES[rpt.stock_type]?.label : 'Custom'} &bull;
                    {rpt.report_data?.summary?.total_animals || '?'} animals &bull;
                    {new Date(rpt.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleViewReport(rpt.id)} className="px-3 py-1.5 text-xs font-medium bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d6b47]" data-testid={`view-report-${rpt.id}`}>
                    <Eye className="w-3.5 h-3.5 inline mr-1" /> View
                  </button>
                  <button onClick={() => handleEmailReport(rpt.id)} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                    <Mail className="w-3.5 h-3.5 inline mr-1" /> Email
                  </button>
                  <button onClick={() => handleDeleteReport(rpt.id)} className="px-2 py-1.5 text-xs text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;
